"use client";
/**
 * Checkout cross-sell card (Block 4, overview 7.6 "Checkout").
 *
 * A single subtle card ABOVE the order summary — never a modal interruption.
 * The ranker (surface "checkout_crosssell", capped at 1 server-side) returns
 * the best add-on offer for the current cart; we show it with [Add] / [No
 * thanks]. "Add" applies the offer through the EXISTING promo path
 * (`setAppliedPromotion`, validated against the branch's active promotions) so
 * the checkout's totals recompute naturally — payment-method and totals logic
 * are untouched. Code-less (category-tied) offers fall back to deep-linking the
 * menu. Dismiss is per-session.
 *
 * Renders nothing unless signed in AND the ranker returns a not-dismissed offer
 * with a usable cart context.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Icons } from "@/components/Icons";
import { OfferIcon, offerUseNowPath } from "@/components/offers/OfferBits";
import { useNextBestOffers, useSessionDismissal } from "@/hooks/useOffers";
import { usePromotionIdentity } from "@/hooks/usePromotions";
import { useAuth } from "@/store/auth";
import { useBranch } from "@/store/branch";
import { useCart } from "@/store/cart";
import { promotionsApi } from "@/api/services";
import { track } from "@/lib/analytics";
import type { NextBestOffer } from "@/api/services";

export function CheckoutCrossSell() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isLoggedIn } = useAuth();
  const { branchId, branchSlug } = useBranch();
  const promoIdentity = usePromotionIdentity();
  const { cart, setAppliedPromotion } = useCart();
  const { isDismissed, dismiss } = useSessionDismissal(branchSlug);
  const [applying, setApplying] = useState(false);

  // Categories currently in the cart drive the ranker's add-on matching.
  const cartCategoryIds = useMemo(() => {
    const ids = new Set<string>();
    for (const line of cart) {
      if (line.categoryId) ids.add(line.categoryId);
    }
    return [...ids];
  }, [cart]);

  const { offers } = useNextBestOffers("checkout_crosssell", {
    branchId,
    cartCategoryIds,
    enabled: cart.length > 0,
  });

  // Single subtle card → show the top not-dismissed offer only.
  const offer = useMemo(
    () => offers.find((o) => !isDismissed(`checkout:${o.promotionId}`)) ?? null,
    [offers, isDismissed],
  );

  if (!isLoggedIn || cart.length === 0 || !offer) return null;

  const onAdd = async (o: NextBestOffer) => {
    track("offer_crosssell_add", { promotion_id: o.promotionId, surface: "checkout_crosssell" });
    // Category-tied (no code) → send the customer to the menu to add the item.
    if (!o.code) {
      navigate("/");
      return;
    }
    if (!branchId || applying) return;
    setApplying(true);
    try {
      const match = await promotionsApi.validateCode(branchId, o.code, promoIdentity);
      if (match) setAppliedPromotion(match);
    } catch (err) {
      console.warn("[offers] checkout cross-sell apply failed", err);
    } finally {
      setApplying(false);
      dismiss(`checkout:${o.promotionId}`);
    }
  };

  const onDecline = () => {
    track("offer_crosssell_declined", {
      promotion_id: offer.promotionId,
      surface: "checkout_crosssell",
    });
    dismiss(`checkout:${offer.promotionId}`);
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: 12,
        borderRadius: "var(--radius-card)",
        background: "var(--primary-50)",
        border: "1px solid var(--primary-100)",
      }}
    >
      <OfferIcon rewardKind={offer.rewardKind} size={38} glyph={18} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--ink-1)" }}>
          {offer.headline}
        </div>
        {offer.description && (
          <div
            style={{
              fontSize: 12,
              color: "var(--ink-2)",
              marginTop: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {offer.description}
          </div>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
        <button
          type="button"
          onClick={() => onAdd(offer)}
          disabled={applying}
          style={{
            height: 32,
            padding: "0 12px",
            borderRadius: "var(--radius-chip)",
            border: 0,
            background: "var(--primary-600)",
            color: "#fff",
            fontSize: 12.5,
            fontWeight: 700,
            cursor: applying ? "default" : "pointer",
            opacity: applying ? 0.6 : 1,
            fontFamily: "inherit",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <Icons.plus size={13} />
          {t("v2.offers.add")}
        </button>
        <button
          type="button"
          onClick={onDecline}
          aria-label={t("v2.offers.no_thanks")}
          style={{
            height: 32,
            padding: "0 10px",
            borderRadius: "var(--radius-chip)",
            border: "1px solid var(--border)",
            background: "transparent",
            color: "var(--ink-2)",
            fontSize: 12.5,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          {t("v2.offers.no_thanks")}
        </button>
      </div>
    </div>
  );
}
